import {
  TableContainer,
  Table,
  TableRow,
  TableCell,
  Checkbox,
  TableHead,
  TableBody
} from "@material-ui/core";

type Column<Key extends string, ItemType extends { [k: string]: any }> = {
  key: Key;
  header: string | JSX.Element;
  render: (value: ItemType[Key], row: ItemType) => JSX.Element | string;
};

/**
 * Renders a table where you can select multiple rows. Example:
 *
 * <MultiSelectTable
 *   data={[
 *     { a: 10, b: "foo" },
 *     { a: 20, b: "bar" }
 *   ]}
 *   columns={[
 *     {
 *       key: "a",
 *       header: "A",
 *       render: (value: number) => <p>{value}</p>
 *     },
 *     {
 *       key: "b",
 *       header: "B",
 *       render: (value: string) => value
 *     }
 *   ]}
 *   primaryKey={"a"}
 *   selectedRows={new Set([10])}
 *   onChangeSelectedRows={(x: Set<number>) => {
 *     console.log(x);
 *   }}
 * />
 */
export default function MultiSelectTable<
  // The type of the object that will be rendered in each row
  ItemType extends { [k: string]: any },
  // The key of the object that will be be used to identify that row in the
  // Set of selected rows (e.g. "id" or "surveyQuestionId")
  PrimaryKey extends keyof ItemType,
  // The type of the value associated with the PrimaryKey (usually a string)
  PrimaryKeyType extends ItemType[PrimaryKey]
>({
  data,
  columns,
  primaryKey,
  selectedRows,
  onChangeSelectedRows,
  allowSelectAll = false,
  disableTableHeader = false,
  containerStyle
}: {
  data: ItemType[];
  columns: {
    [K in keyof ItemType]: Column<K & string, ItemType>;
  }[keyof ItemType][];
  primaryKey: PrimaryKey;
  selectedRows: Set<PrimaryKeyType>;
  onChangeSelectedRows: (newRows: Set<PrimaryKeyType>) => void;
  allowSelectAll?: boolean;
  disableTableHeader?: boolean;
  containerStyle: React.CSSProperties;
}): JSX.Element {
  function handleClick(row: ItemType) {
    const newSelectedRows = new Set(selectedRows);
    if (selectedRows!.has(row[primaryKey])) {
      newSelectedRows.delete(row[primaryKey]);
    } else {
      newSelectedRows.add(row[primaryKey]);
    }

    onChangeSelectedRows(newSelectedRows);
  }

  function handleSelectAllClicked() {
    if (selectedRows.size === data.length) {
      // all were selected, so unselect them all
      onChangeSelectedRows(new Set());
    } else {
      // otherwise, select them all
      onChangeSelectedRows(new Set(data.map(d => d[primaryKey])));
    }
  }

  return (
    <TableContainer style={containerStyle}>
      <Table stickyHeader size="medium">
        {!disableTableHeader && (
          <TableHead>
            <TableRow>
              {allowSelectAll ? (
                <TableCell
                  style={{ backgroundColor: "white" }}
                  padding="checkbox"
                >
                  <Checkbox
                    checked={selectedRows.size === data.length}
                    onChange={handleSelectAllClicked}
                  />
                </TableCell>
              ) : (
                <TableCell style={{ backgroundColor: "white" }} />
              )}
              {columns.map(({ header, key }) => (
                <TableCell
                  key={key}
                  padding="none"
                  style={{ backgroundColor: "white" }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {data.map(row => (
            <TableRow
              key={row[primaryKey]}
              hover
              onClick={() => handleClick(row)}
              role="checkbox"
              tabIndex={-1}
              selected={selectedRows.has(row[primaryKey])}
            >
              <TableCell padding="checkbox">
                <Checkbox checked={selectedRows.has(row[primaryKey])} />
              </TableCell>
              {columns.map(({ key, render }) => (
                <TableCell key={key}>{render(row[key], row)}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
